/**
 *  [2022] Zettant Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Zettant Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Zettant Incorporated and its
 * suppliers and may be covered by Japan. and Foreign Patents,
 * patents in process, and are protected by trade secret or
 * copyright law. Dissemination of this information or
 * reproduction of this material is strictly forbidden unless
 * prior written permission is obtained from Zettant Incorporated.
 */

/**
 * :ZNOTE:
 * tag: cacheManage
 * note:
 *  - cacheManageのMedia情報を管理するためのクラス
 *  - CacheManageの見通しをよくするために機能を分割しただけなので、CacheManage以外からこのクラスを参照することはないはず
 * sample:
 *  - ほぼ全てのプロジェクト
 * :ZNOTE:
 */


import {Base64} from 'js-base64';
import pako  from 'pako';
import {Dexie} from 'dexie';
import {InMemoryCache} from './inMemoryCache';
import {TMediaInfo, TMediaUpload, TRecordWithMedia} from './interfaces';


class CacheManageMedia {
  protected db: Dexie.Table;
  protected memCache: InMemoryCache;
  protected readonly primaryKeyName: string;
  protected readonly secondaryKeyName: string;
  private readonly putData: (primaryKeyValue: string, datToRegister: any)=>Promise<void>;
  public putMediaAdapter = async (record: any, fileName: string, media: any): Promise<[string, string, number, number]> => [fileName, fileName, (new Date()).getTime(), -1];  // [ファイル名, ファイルパス, 作成時刻, サイズ]
  public bulkPutMediaAdapter = async (dat: TMediaUpload[]): Promise<{primaryKey: string, secondaryKey: string, mediaInfo: [string, string, number, number]}[]> =>  // [ファイル名, ファイルパス, 作成時刻, サイズ]
    dat.map((d) => ({
      primaryKey: d.record[this.primaryKeyName],
      secondaryKey: d.record[this.secondaryKeyName],
      mediaInfo: [d.fileName, d.fileName, (new Date()).getTime(), -1]
    }));
  public getMediaAdapter = async (path: string): Promise<any> => `xxxxxxxxx${path}`;
  public deleteMediaAdapter = async (record: any, fileName: string): Promise<any> => true;

  /**
   * コンストラクタ
   * @param db {Dexie.Table}
   * @param memCache {InMemoryCache}
   * @param primaryKeyName {string}
   * @param secondaryKeyName {string}
   * @param putData {(primaryKeyValue: string, datToRegister: any)=>Promise<void>}  CacheManageのputDataメソッド
   */
  constructor(db: Dexie.Table, memCache: InMemoryCache, primaryKeyName: string, secondaryKeyName: string, putData: (primaryKeyValue: string, datToRegister: any)=>Promise<void>) {
    this.db = db;
    this.memCache = memCache;
    this.primaryKeyName = primaryKeyName;
    this.secondaryKeyName = secondaryKeyName;
    this.putData = putData;

    if ((this.db as any).setClearCallback) {
      // localStorageを用いる場合、容量オーバーが比較的頻繁に発生する可能性があるので、clear時にlatestもクリアする
      (this.db as any).setClearCallback(() => this.memCache.clear());
    }
  }

  /**
   * cacheManageをflushした後に再初期化する
   * @param db
   * @param memCache
   */
  public reInitialize(db: Dexie.Table, memCache: InMemoryCache) {
    this.db = db;
    this.memCache = memCache;
  }

  /**
   * メディアファイル本体を取得する
   * @param record {any}
   * @param fileName {string}
   */
  public getMedia = async (record: any, fileName: string): Promise<Uint8Array|undefined> => {
    if (!record.dataPaths || !(fileName in record.dataPaths)) return;

    const mediaInfo = await this.db.get(record.dataPaths[fileName][0]);
    if (mediaInfo && mediaInfo.timestamp === record.dataPaths[fileName][1]) {
      await this.db.update(record.dataPaths[fileName][0], {lastAccessedAt: (new Date()).getTime()});
      const compressed = Base64.toUint8Array(mediaInfo.content);
      return pako.inflate(compressed);
    }

    // EXP: キャッシュされていない、またはキャッシュが古かった場合はサーバから取得する
    const path = record.dataPaths[fileName][0];
    const content = await this.getMediaAdapter(path);  // respはzlib圧縮されたバイナリデータをBase64化したもの
    if (!content) return;
    await this.updateMediaCache(record, fileName, content); // EXP: キャッシュを更新する
    const compressed = Base64.toUint8Array(content);
    return pako.inflate(compressed);
  };

  /**
   * メディアファイルを更新する
   * @param record {any}
   * @param fileName {string}
   * @param mediaBin {Uint8Array}
   */
  public updateMedia = async (record: any, fileName: string, mediaBin: Uint8Array): Promise<boolean> => {
    if (!record.dataPaths || !(fileName in record.dataPaths)) return false;
    record.updatedAt = new Date().getTime();
    const mediaBase64 = Base64.fromUint8Array(pako.deflate(mediaBin));
    const mediaInfo = await this.putMediaAdapter(record, fileName, mediaBase64);
    record.dataPaths[mediaInfo[0]] = [mediaInfo[1], mediaInfo[2], mediaInfo[3]];  // [ファイルパス, 作成時刻, サイズ]
    await Promise.all([
      this.putData(record[this.primaryKeyName], record),
      this.updateMediaCache(record, fileName, mediaBase64), // EXP: キャッシュを更新する
    ]);
    return true;
  };

  /**
   * メディアファイルをまとめて更新する
   * @param dat {{record: any, fileName: string, media: Uint8Array}[]}
   */
  public bulkUpdateMedia = async (dat: {record: any, fileName: string, media: Uint8Array}[]): Promise<boolean> => {
    const datToRegister: any = [];
    for (const d of dat) {
      if (!d.record.dataPaths || !(d.fileName in d.record.dataPaths)) return false;
      const mediaBase64 = Base64.fromUint8Array(pako.deflate(d.media));
      d['mediaBase64'] = mediaBase64;
      datToRegister.push({record: d.record, fileName: d.fileName, media: mediaBase64});
    }
    const res = await this.bulkPutMediaAdapter(datToRegister);

    const promises: any = [];
    for (const d of dat) {
      const r = res.find((ent) => ent.primaryKey == d.record[this.primaryKeyName] && ent.secondaryKey == d.record[this.secondaryKeyName]);
      if (!r) continue;
      d.record.dataPaths[r.mediaInfo[0]] = [r.mediaInfo[1], r.mediaInfo[2], r.mediaInfo[3]]; // [ファイルパス、作成時刻、サイズ]
      promises.push(this.putData(d.record[this.primaryKeyName], d.record));
      promises.push(this.updateMediaCache(d.record, r.mediaInfo[0], d['mediaBase64']));
    }
    await Promise.all(promises);
    return true;
  };

  /**
   * メディアファイルを削除する
   * @param record {any}
   * @param fileName {string}
   */
  public deleteMedia = async (record: any, fileName: string): Promise<boolean> => {
    if (!record.dataPaths || !(fileName in record.dataPaths)) return false;
    record.updatedAt = new Date().getTime();
    await Promise.all([
      this.deleteMediaAdapter(record, fileName),  // EXP: AWS上では、S3のファイルも、DynamoDBテーブルの情報も両方削除される
      this.updateMediaCache(record, fileName), // EXP: キャッシュ上のデータを削除する
    ]);
    delete record.dataPaths[fileName];
    if (Object.keys(record.dataPaths).length === 0) delete record['dataPaths'];

    await this.putData(record[this.primaryKeyName], record);
    return true;
  };

  /**
   * 画像キャッシュを更新する
   * @param record {any}
   * @param fileName {string}
   * @param mediaB64 {string} undefinedの時はキャッシュを削除する
   * @private
   */
  public async updateMediaCache (record: any, fileName: string, mediaB64?: string) {
    if (mediaB64) {
      await this.db.put({filePath: record.dataPaths[fileName][0], content: mediaB64, timestamp: record.dataPaths[fileName][1], lastAccessedAt: (new Date()).getTime()});
    } else {
      await this.db.delete(record.dataPaths[fileName][0]);
    }
  }

  /**
   * 指定されたrecordのdataPathsにセットされているファイル情報をローカルから削除する
   * @param record {any}
   * @protected
   */
  public deleteAllRelatedMedia = async (record: any) => {
    if (!record.dataPaths || Object.keys(record.dataPaths).length === 0) return;
    await Promise.all(
      Object.keys(record.dataPaths).map((fileName) => {
        this.updateMediaCache(record, fileName);
      }),
    );
  };


  /**
   * 単一のレコードに紐づいたメディア情報本体をサーバとローカルキャッシュに登録する
   * @param record {any}
   * @param media {TMediaInfo}
   */
  public registerMediaInfoForSingleRecord = async (record: any, media: TMediaInfo) => {
    const tmpB64: any = {};

    // TODO: localstackでテストする場合はこちらはコメントアウトする
    const promises = [] as Promise<any>[];
    Object.keys(media).map((fileName) => {
      tmpB64[fileName] = Base64.fromUint8Array(pako.deflate(media[fileName]));
      promises.push(this.putMediaAdapter(record, fileName, tmpB64[fileName]));
    });
    const mediaResults = await Promise.all(promises);
    // // TODO: localstackを使う場合は、promise.allで並列化すると動かないのでこちらを使う
    // const mediaResults: any = [];
    // for (const fileName of Object.keys(media)) {
    //   tmpB64[fileName] = Base64.fromUint8Array(pako.deflate(media[fileName]));
    //   const res = await this.putMediaAdapter(record, fileName, tmpB64[fileName]);
    //   mediaResults.push(res);
    // }

    // OBJ: ローカルキャッシュにメディア本体を登録する
    const promisesMedia: any = [];
    mediaResults.forEach((m) => {
      record.dataPaths[m[0]][1] = m[2];  // timestampのみ更新
      promisesMedia.push(this.db.put({
        filePath: m[1],
        content: tmpB64[m[0]],
        timestamp: m[2],
        lastAccessedAt: (new Date()).getTime()
      }));
    });
    await Promise.all(promisesMedia);
  };

  /**
   * 複数のレコードに紐づいたメディア情報本体をサーバとローカルキャッシュに登録する
   * @param valueWithMedia {TRecordWithMedia[]}
   */
  public registerMediaInfoForMultipleRecords = async (valueWithMedia: TRecordWithMedia[]) => {
    const mediaToRegister: TMediaUpload[] = [];
    // OBJ: サーバにメディア本体を登録する
    for (const val of valueWithMedia) {
      if (!val.media) continue;
      Object.keys(val.media).map((fileName) => {
        const mediaB64 = Base64.fromUint8Array(pako.deflate((val.media as any)[fileName]));
        mediaToRegister.push({record: val.record, fileName, media: mediaB64});
      });
    }
    if (mediaToRegister.length === 0) return;  // メディアは何もないので処理終了
    const mediaResults = await this.bulkPutMediaAdapter(mediaToRegister);

    // OBJ: ローカルキャッシュにメディア本体を登録する
    const promisesMedia = [] as Promise<any>[];
    mediaResults.forEach((m) => {
      const value = mediaToRegister.find((v) => v.record[this.primaryKeyName] === m.primaryKey && v.record[this.secondaryKeyName] === m.secondaryKey && v.fileName === m.mediaInfo[0]);
      if (!value) return;
      value.record.dataPaths[m.mediaInfo[0]] = [m.mediaInfo[1], m.mediaInfo[2], m.mediaInfo[3]];  // [ファイル名, ファイルパス, 作成時刻, サイズ]
      promisesMedia.push(this.db.put({
        filePath: m.mediaInfo[1],
        content: value.media,
        timestamp: m.mediaInfo[2],
        lastAccessedAt: (new Date()).getTime()
      }));
    });
    await Promise.all(promisesMedia);
  };
}

export {CacheManageMedia};
